import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import CustomButton from "./Button";
import Loader from "./Loader";
import { useToast } from "./Toast";
import apiService from "../Services/api.service";
import { UI, btnSx } from "./ActionDrawer";

const Btn = ({ variant = "solid", sx, ...props }) => (
  <CustomButton {...props} sx={{ ...btnSx(variant, props.disabled), ...sx }} />
);

/* A technician login (created via Postman today, no signup flow exists) is
   just a principalAccount with role "technician" — it isn't assignable to
   anything until a Technician roster doc also exists, linked back to it.
   This is that missing link, self-serve. */
const AddTechnicianModal = ({ open, onClose, onCreated }) => {
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [name, setName] = useState("");
  const [base, setBase] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAccountId("");
    setName("");
    setBase("");
    setPhone("");
    setLoading(true);
    apiService
      .fetchUnlinkedTechnicianAccounts(token)
      .then((res) => setAccounts(res.data || []))
      .catch(() => toast.error("Failed to load technician logins"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAccountChange = (value) => {
    setAccountId(value);
    const account = accounts.find((a) => a.id === value);
    if (account && !name) setName(account.name || "");
  };

  const handleSubmit = async () => {
    if (!accountId) {
      toast.error("Select a login account");
      return;
    }
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      await apiService.createTechnician({ accountId, name, base, phone }, token);
      toast.success("Technician added to the roster");
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add technician");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: UI.surface,
          backgroundImage: "none",
          border: "1px solid",
          borderColor: UI.line,
          borderRadius: "10px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "10px",
          padding: "16px 18px",
          borderBottom: "1px solid",
          borderColor: UI.line,
        }}
      >
        <Box>
          <Box sx={{ fontSize: 16, fontWeight: 500, color: UI.ink }}>
            Add technician
          </Box>
          <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "2px" }}>
            Links an existing technician login to the assignment roster.
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "18px", bgcolor: UI.bg2 }}>
        {loading ? (
          <Box sx={{ py: 4 }}>
            <Loader />
          </Box>
        ) : accounts.length === 0 ? (
          <Box sx={{ fontSize: 13, color: UI.ink3 }}>
            No unlinked technician logins found. Every account with the
            technician role is already on the roster.
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <TextField
              select
              label="Login account"
              value={accountId}
              onChange={(e) => handleAccountChange(e.target.value)}
              fullWidth
              size="small"
            >
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name || account.email || account.id}
                  {account.email ? ` · ${account.email}` : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Base city"
              value={base}
              onChange={(e) => setBase(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
              size="small"
            />
          </Box>
        )}
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          padding: "14px 18px",
          borderTop: "1px solid",
          borderColor: UI.line,
        }}
      >
        <Btn text="Cancel" variant="ghost" onClick={onClose} />
        {accounts.length > 0 && (
          <Btn
            text={submitting ? "Adding…" : "Add technician"}
            onClick={handleSubmit}
            disabled={submitting}
          />
        )}
      </Box>
    </Dialog>
  );
};

export default AddTechnicianModal;
